import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import i18n from "../../../language/i18n";
import { normalizeLang } from "../../../utils/localeDataLoader";
import {
  filterIntroduceCardsBySubtitle,
  INTRODUCE_CARD_GROUPS,
  useIntroduceOverviewCardsQuery,
  useIntroducePoliciesQuery,
} from "hooks";
import IntroduceCardGrid from "./IntroduceCardGrid";
import IntroduceSectionTitle from "./IntroduceSectionTitle";
import IntroduceStandaloneLayout from "./IntroduceStandaloneLayout";

const splitPolicyDescription = (html = "") => {
  if (!html || typeof window === "undefined") {
    return { section1: "", section2: "", section3: "" };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const children = Array.from(doc.body.firstElementChild?.children || []);
  const sections = { section1: [], section2: [], section3: [] };
  let activeSection = "section1";

  children.forEach((child) => {
    const text = child.textContent?.trim() || "";

    if (/^1[.)\s]/.test(text)) activeSection = "section1";
    if (/^2[.)\s]/.test(text)) activeSection = "section2";
    if (/^3[.)\s]/.test(text)) activeSection = "section3";

    sections[activeSection].push(child.outerHTML);
  });

  return {
    section1: sections.section1.join(""),
    section2: sections.section2.join(""),
    section3: sections.section3.join(""),
  };
};

const splitPolicyHeading = (html = "") => {
  if (!html || typeof window === "undefined") {
    return { heading: "", content: "" };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const children = Array.from(doc.body.firstElementChild?.children || []);

  return {
    heading: children[0]?.outerHTML || "",
    content: children.slice(1).map((child) => child.outerHTML).join(""),
  };
};

function IntroducePolicies() {
  useTranslation();
  const location = useLocation();
  const currentLang = normalizeLang(i18n.language);
  const isStandalone = /^\/(tuyen-dung|recruitment)(\/|$)/.test(
    location.pathname,
  );
  const {
    data: policiesData,
    isLoading: loading,
    error,
  } = useIntroducePoliciesQuery(currentLang);
  const { data: overviewCards = [] } =
    useIntroduceOverviewCardsQuery(currentLang);
  const salaryCards = filterIntroduceCardsBySubtitle(
    overviewCards,
    INTRODUCE_CARD_GROUPS.salaryBenefits,
  );
  const policySections = useMemo(
    () => splitPolicyDescription(policiesData?.description),
    [policiesData?.description],
  );
  const policyRecruitment = useMemo(
    () => splitPolicyHeading(policySections.section1),
    [policySections.section1],
  );
  const policyTraining = useMemo(
    () => splitPolicyHeading(policySections.section3),
    [policySections.section3],
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return (
      <div style={{ color: "red", textAlign: "center", padding: "20px" }}>
        Không thể tải dữ liệu. Vui lòng thử lại sau.
      </div>
    );
  }

  if (!policiesData) {
    return <div>Không có dữ liệu.</div>;
  }

  const content = (
    <div className="IntroducePolicies">
      <IntroduceSectionTitle>{policiesData.title}</IntroduceSectionTitle>
      {policyRecruitment.heading && (
        <div
          className="policy-section-heading quill-content"
          dangerouslySetInnerHTML={{ __html: policyRecruitment.heading }}
        />
      )}
      {policyRecruitment.content && (
        <blockquote className="policy-quote">
          <div
            className="introducePolicies-right-text policies-description quill-content"
            dangerouslySetInnerHTML={{ __html: policyRecruitment.content }}
          />
        </blockquote>
      )}
      {policySections.section2 && (
        <div
          className="policy-section-heading quill-content"
          dangerouslySetInnerHTML={{ __html: policySections.section2 }}
        />
      )}
      <IntroduceCardGrid cards={salaryCards} className="policy-card-grid" />
      {policyTraining.heading && (
        <div
          className="policy-section-heading quill-content"
          dangerouslySetInnerHTML={{ __html: policyTraining.heading }}
        />
      )}
      {policyTraining.content && (
        <blockquote className="policy-quote">
          <div
            className="introducePolicies-right-text policies-description quill-content"
            dangerouslySetInnerHTML={{ __html: policyTraining.content }}
          />
        </blockquote>
      )}
    </div>
  );

  if (!isStandalone) return content;

  return (
    <IntroduceStandaloneLayout
      eyebrow={currentLang === "vi" ? "TUYỂN DỤNG HSV" : "CAREERS AT HSV"}
      title={
        currentLang === "vi" ? "Chính sách nhân sự" : "Human resources policies"
      }
      description={
        currentLang === "vi"
          ? "Chính sách tuyển dụng, đãi ngộ và phát triển năng lực dành cho đội ngũ HSV."
          : "Recruitment, benefits and professional development policies at HSV."
      }
    >
      {content}
    </IntroduceStandaloneLayout>
  );
}

export default IntroducePolicies;
